import { z } from "zod";

/**
 * Feedback aus dem laufenden CRM — legt ein Issue im Forgejo-Repo an.
 *
 * Der Knopf sitzt im Kopf jeder Seite und ist bewusst **nicht** an eine Rolle
 * gebunden: Wer das Tool benutzt, darf melden, was ihm auffällt. Geschrieben
 * wird unter einem Forgejo-Token einer Person (Host-Secret), deshalb nennt der
 * Rumpf des Issues den **tatsächlichen** Absender — der Autor in Forgejo ist
 * immer derselbe und sagt über die Herkunft nichts aus.
 */

/**
 * Die drei Kategorien. Sie sind zugleich Präfix im Titel (`[Fehler] …`) und
 * Label am Issue — ein Wert, zwei Wirkungen, damit beides nie auseinanderläuft.
 *
 * **Deutsch, und das bleibt so.** Der Wert ist die Kennung des Labels im
 * Repo; ein übersetzter Wert hieße, dass dasselbe Anliegen je nach
 * Oberflächensprache unter zwei Labels landet. Übersetzt wird nur die
 * Beschriftung im Web (`feedback.category.*`).
 */
export const FEEDBACK_CATEGORIES = ["fehler", "wunsch", "frage"] as const;
export const feedbackCategorySchema = z.enum(FEEDBACK_CATEGORIES);
export type FeedbackCategory = z.infer<typeof feedbackCategorySchema>;

/** Das Label, das jedes über diesen Weg angelegte Issue trägt. */
export const FEEDBACK_LABEL = "feedback";

/**
 * Farben der Labels, falls sie im Repo noch nicht existieren — dann legt die
 * API sie an. `feedback` trägt das mindsquare-Orange, die drei Kategorien
 * lehnen sich an die gewohnten GitHub-/Forgejo-Farben an.
 */
export const FEEDBACK_LABEL_COLORS: Readonly<Record<string, string>> = {
  feedback: "#EA5B0B",
  fehler: "#d73a4a",
  wunsch: "#0075ca",
  frage: "#fdc300",
};

/** Rumpf von `POST /feedback`. */
export const feedbackRequestSchema = z.object({
  category: feedbackCategorySchema,
  /** Die Überschrift des Issues, ohne Kategorie-Präfix — das setzt die API. */
  title: z.string().trim().min(3).max(120),
  /** Der eigentliche Text. Er landet im Issue und **nie** im Audit-Eintrag. */
  text: z.string().trim().min(1).max(5000),
  /**
   * Wo der Melder stand: `location.pathname + location.search`.
   *
   * **Ein Pfad, und nichts sonst.** Der Wert landet im Issue in einem
   * Code-Span; ein Backtick oder ein Zeilenumbruch darin bräche den Span auf
   * und der Rest der Zeile wäre wieder Markdown. Deshalb hier eng geprüft —
   * führender Schrägstrich, kein Leerraum, kein Backtick — statt sich drüben
   * auf das Entschärfen zu verlassen: Was gar nicht erst hereinkommt, muss
   * niemand später unschädlich machen. `?`, `&` und `=` sind erlaubt, sonst
   * wäre `/wartung?onlyMine=true` kein gültiger Wert.
   */
  page: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .regex(/^\/[^\s`]*$/, "Erwartet wird ein Pfad wie /wartung?onlyMine=true"),
  /**
   * Die Version, die im **Browser** geladen ist. Nach einem Deploy kann sie von
   * der des Servers abweichen — genau dann ist die Abweichung die Information,
   * die eine Fehlermeldung erklärt. Der Serverstand steht ohnehin daneben.
   */
  appVersion: z.string().trim().min(1).max(100),
});
export type FeedbackRequest = z.infer<typeof feedbackRequestSchema>;

/** Antwort von `POST /feedback` — Nummer und Adresse des angelegten Issues. */
export const feedbackCreatedSchema = z.object({
  number: z.number().int().positive(),
  url: z.string().url(),
});
export type FeedbackCreated = z.infer<typeof feedbackCreatedSchema>;

/**
 * Antwort von `GET /feedback/config`. Ohne Token meldet das Modul
 * `configured:false`, und der Knopf im Kopf bleibt weg — statt auf einen
 * Dialog zu führen, der beim Absenden 503 antwortet.
 */
export const feedbackConfigResponseSchema = z.object({ configured: z.boolean() });
/**
 * Heißt bewusst `…Response`: Die API führt unter `FeedbackConfig` etwas
 * anderes — Adresse, Repo und Token aus der Umgebung (`feedback.config.ts`).
 * Zwei gleichnamige Typen mit verschiedenem Inhalt im selben Modul sind die
 * Art Kollision, die erst beim dritten Leser auffällt.
 */
export type FeedbackConfigResponse = z.infer<typeof feedbackConfigResponseSchema>;

/** Obergrenze je Screenshot. Der Rumpf wird komplett im Speicher gepuffert. */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;

/** Erlaubte Bildtypen. Bewusst ohne SVG — das führt beim Anzeigen Skript aus. */
export const FEEDBACK_SCREENSHOT_TYPES = ["image/png", "image/jpeg"] as const;

/**
 * Prüft einen Screenshot, bevor er auf die Reise geht — dieselbe Regel im Web
 * (Dateiauswahl **und** Einfügen aus der Zwischenablage) wie in der API.
 *
 * Gibt den Grund zurück, aus dem die Datei nicht taugt, oder `null`. Ein
 * Wahrheitswert wäre hier zu wenig: Die Oberfläche muss sagen können, ob das
 * Format, die Größe oder ein leerer Inhalt das Problem ist, sonst probiert der
 * Nutzer dasselbe Bild ein zweites Mal.
 *
 * **Der Typ ist hier nur die ANGABE des Clients.** Sie taugt zum frühen
 * Abweisen, nicht als Beleg: Die API prüft zusätzlich die Magic Number des
 * Inhalts und leitet die Endung daraus ab (`bildtyp.ts`).
 */
export function feedbackScreenshotProblem(datei: {
  type: string;
  size: number;
}): "type" | "size" | "empty" | null {
  const typ = datei.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (!(FEEDBACK_SCREENSHOT_TYPES as readonly string[]).includes(typ)) return "type";
  // Leer ist ein eigener Grund, nicht „zu groß": „Das Bild darf höchstens 5 MB
  // groß sein" als Antwort auf eine 0-Byte-Datei schickt den Melder in die
  // falsche Richtung — er macht das Bild kleiner, und es bleibt leer.
  if (datei.size <= 0) return "empty";
  if (datei.size > FEEDBACK_SCREENSHOT_MAX_BYTES) return "size";
  return null;
}
